import { PortalDropdown } from '@/components/ui/PortalDropdown'
import { Spinner } from '@/components/ui/spinner'
import { useSecurityModal } from '@/hooks/useSecurityModal'
import { fetchPaymentPortal } from '@/lib/api'
import {
  useGetImpersonationStatusQuery,
  useGetUserAdminStatusQuery,
  useSignOutMutation,
  useStopImpersonationMutation,
} from '@/lib/features/user/userApi'
import { PAGE_PATH } from '@/lib/page-paths'
import { hasInfiniteCredits } from '@/lib/subscription-utils'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { FiCreditCard, FiLink, FiLogOut, FiSettings, FiShield, FiUserX } from 'react-icons/fi'
import { toast } from 'react-toastify'
import type { ApiUserQuota } from 'sprites-types/api/user-quota'
import type { ApiUser } from 'sprites-types/api/users'

// Hook to animate number counting with proper cleanup and edge case handling
const useCountAnimation = (targetValue: number | null | undefined, duration: number = 800) => {
  const [displayValue, setDisplayValue] = useState(targetValue ?? 0)
  const rafIdRef = useRef<number | null>(null)

  useEffect(() => {
    // Handle null/undefined/non-finite values
    if (targetValue == null || !Number.isFinite(targetValue)) {
      setDisplayValue(targetValue ?? 0)
      return
    }

    // If target hasn't changed significantly, don't animate
    if (targetValue === displayValue) {
      return
    }

    const startValue = displayValue
    const startTime = performance.now()

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Ease-out cubic function for smooth deceleration
      const easeOutCubic = 1 - Math.pow(1 - progress, 3)
      const currentValue = Math.round(startValue + (targetValue - startValue) * easeOutCubic)

      setDisplayValue(currentValue)

      if (progress < 1) {
        rafIdRef.current = requestAnimationFrame(animate)
      } else {
        rafIdRef.current = null
      }
    }

    rafIdRef.current = requestAnimationFrame(animate)

    // Cleanup function to cancel animation if component unmounts or targetValue changes
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [targetValue, duration])

  return displayValue
}

type SidebarProfileDropdownProps = {
  isOpen: boolean
  onClose: () => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
  user: ApiUser
  quota: ApiUserQuota | null
  onOpenIntegrations: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export function SidebarProfileDropdown({
  isOpen,
  onClose,
  buttonRef,
  user,
  quota,
  onOpenIntegrations,
  onMouseEnter,
  onMouseLeave,
}: SidebarProfileDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isBillingLoading, setIsBillingLoading] = useState(false)
  const [signOutMutation] = useSignOutMutation()
  const [stopImpersonation, { isLoading: isStoppingImpersonation }] = useStopImpersonationMutation()
  const router = useRouter()
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const { setOpen: openSecurityModal } = useSecurityModal()
  const { data: adminStatus } = useGetUserAdminStatusQuery()
  const { data: impersonationStatus } = useGetImpersonationStatusQuery()

  // Animate credit count changes (only for finite numbers, not "unlimited")
  const creditsValue = typeof quota?.credits === 'number' ? quota.credits : null
  const animatedCredits = useCountAnimation(creditsValue)

  // Calculate dropdown position relative to sidebar (bottom-anchored)
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return

    const updatePosition = () => {
      const sidebarWidth = buttonRef.current?.closest('aside')?.getBoundingClientRect().width || 280
      const dropdownHeight = dropdownRef.current?.getBoundingClientRect().height || 500
      const bottomPadding = 16 // pixels from bottom
      const leftPadding = 8 // pixels from sidebar

      setDropdownPosition({
        top: window.innerHeight - dropdownHeight - bottomPadding,
        left: sidebarWidth + leftPadding,
      })
    }

    // Update position after a brief delay to ensure dropdown is rendered
    setTimeout(updatePosition, 0)
    window.addEventListener('resize', updatePosition)
    return () => window.removeEventListener('resize', updatePosition)
  }, [isOpen, buttonRef])

  const handleSignOut = async () => {
    await signOutMutation()
    router.push(PAGE_PATH.HOME)
    onClose()
  }

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation().unwrap()
      router.push('/seo-admin')
      onClose()
    } catch (error) {
      console.error('Failed to stop impersonation:', error)
      toast.error('Failed to stop impersonation')
    }
  }

  const handleBillingClick = async () => {
    if (user.isAnonymous) {
      toast.error('You must sign in to manage your billing')
      return
    }

    if (quota?.subscription.name === 'Free') {
      router.push(PAGE_PATH.PLANS)
      return
    }

    setIsBillingLoading(true)
    try {
      const portalUrl = await fetchPaymentPortal('')
      if (portalUrl) {
        window.location.href = portalUrl
      }
    } catch (error) {
      console.error('Error fetching payment portal:', error)
      toast.error('Error fetching payment portal')
      setIsBillingLoading(false)
    }

    setTimeout(() => {
      setIsBillingLoading(false)
    }, 2000)
  }

  return (
    <PortalDropdown
      isOpen={isOpen}
      position={dropdownPosition}
      dropdownRef={dropdownRef}
      strategy="fixed"
    >
      <div
        className="max-h-[80vh] w-[280px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Limited time offer banner for free plan users */}
        {quota?.subscription.name === 'Free' && (
          <a
            href="https://calendly.com/spritesai/sprites-demo"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:bg-ui-hover block border-b border-gray-200 p-3 transition-colors"
          >
            <div className="mb-1.5 inline-block rounded-full bg-purple-100 px-2.5 py-0.5">
              <span className="text-xs font-medium text-purple-900">Limited time offer</span>
            </div>
            <div className="text-ui-text text-sm font-semibold">Free 1:1 onboarding call</div>
            <div className="text-ui-secondary mt-0.5 text-xs">Jump start your success today!</div>
          </a>
        )}

        {/* User info */}
        <div className="border-b border-gray-200 p-4">
          <div className="text-ui-text truncate text-sm font-medium">
            {user.name || user.email?.split('@')[0]}
          </div>
          <div className="text-ui-secondary truncate text-xs">{user.email}</div>
        </div>

        {/* Credits */}
        {quota && (
          <div className="border-b border-gray-200 p-4">
            <div className="bg-ui-hover flex items-center justify-between rounded-lg px-3 py-2">
              <span className="text-ui-secondary text-sm font-medium">Credits</span>
              <span className="text-ui-text text-sm font-semibold">
                {hasInfiniteCredits(quota) ? 'Unlimited' : animatedCredits.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        {/* Menu items */}
        <div className="p-2">
          <button
            onClick={handleBillingClick}
            className="text-ui-text hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition-colors"
          >
            {isBillingLoading && <Spinner variant="default" size={16} overlay={true} />}
            <FiCreditCard className="text-ui-secondary h-4 w-4" />
            Manage plan
          </button>
          <button
            onClick={() => {
              onOpenIntegrations()
              onClose()
            }}
            className="text-ui-text hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition-colors"
          >
            <FiLink className="text-ui-secondary h-4 w-4" />
            Integrations
          </button>
          <button
            onClick={() => {
              openSecurityModal()
              onClose()
            }}
            className="text-ui-text hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition-colors"
          >
            <FiShield className="text-ui-secondary h-4 w-4" />
            Security
          </button>
          {adminStatus?.isAdmin && (
            <button
              onClick={() => {
                router.push('/seo-admin')
                onClose()
              }}
              className="text-ui-text hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition-colors"
            >
              <FiSettings className="text-ui-secondary h-4 w-4" />
              Admin Panel
            </button>
          )}
          {impersonationStatus?.isImpersonating ? (
            <button
              onClick={handleStopImpersonation}
              disabled={isStoppingImpersonation}
              className="text-ui-text hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition-colors disabled:opacity-50"
            >
              {isStoppingImpersonation ? (
                <Spinner variant="simple" size={16} color="#6b7280" />
              ) : (
                <FiUserX className="text-ui-secondary h-4 w-4" />
              )}
              Stop Impersonating
            </button>
          ) : (
            <button
              onClick={handleSignOut}
              className="text-ui-text hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition-colors"
            >
              <FiLogOut className="text-ui-secondary h-4 w-4" />
              Sign Out
            </button>
          )}
        </div>
      </div>
    </PortalDropdown>
  )
}
